class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      // Check if cart interactions are disabled
      if (this.isCartDisabled()) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }

  isCartDisabled() {
    return window.location.pathname === '/cart' ||
      document.body.classList.contains('template-cart');
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    // Check if we should disable all cart interactions
    this.isCartDisabled = this.shouldDisableCart();

    if (!this.isCartDisabled) {
      const debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, ON_CHANGE_DEBOUNCE_TIMER);

      this.addEventListener('change', debouncedOnChange.bind(this));
    }
  }

  cartUpdateUnsubscriber = undefined;

  shouldDisableCart() {
    // Disable cart interactions on main cart detail page
    return window.location.pathname.includes('/cart') ||
      document.body.classList.contains('template-cart');
  }

  connectedCallback() {
    if (this.isCartDisabled) {
      this.disableAllCartInteractions();
      return;
    }

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  disableAllCartInteractions() {
    // Hide all cart interaction buttons permanently
    setTimeout(() => {
      // Hide quantity input fields and make them readonly
      const quantityInputs = this.querySelectorAll('input[type="number"], input[name*="quantity"]');
      quantityInputs.forEach(input => {
        input.readOnly = true;
        input.style.pointerEvents = 'none';
        input.style.cursor = 'default';
      });

      // Hide all quantity buttons (plus/minus)
      const quantityButtons = document.querySelectorAll('.quantity__button, [name="plus"], [name="minus"]');
      quantityButtons.forEach(button => {
        button.style.display = 'none';
      });

      // Hide all remove buttons
      const removeButtons = document.querySelectorAll('cart-remove-button, .cart-remove');
      removeButtons.forEach(button => {
        button.style.display = 'none';
      });

      // Hide any cart action buttons in forms
      const cartForms = document.querySelectorAll('form[action*="/cart"]');
      cartForms.forEach(form => {
        const actionButtons = form.querySelectorAll('button:not([type="submit"]):not(.cart-note-button), input[type="button"]');
        actionButtons.forEach(button => {
          if (!button.classList.contains('cart-note') && !button.classList.contains('checkout-button')) {
            button.style.display = 'none';
          }
        });
      });

      // Hide quantity controls container if exists
      const quantityControls = document.querySelectorAll('.quantity-controls, .cart-item__quantity-controls');
      quantityControls.forEach(control => {
        const buttons = control.querySelectorAll('.quantity__button, [name="plus"], [name="minus"]');
        if (buttons.length > 0) {
          buttons.forEach(btn => btn.style.display = 'none');
        }
      });
    }, 100);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    if (this.isCartDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-detail',
        section: 'main-cart-detail',
        selector: '#main-cart-detail',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    if (this.isCartDisabled) {
      return false;
    }
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text')
      message ? errorText.classList.remove('d-none'):  errorText.classList.add('d-none')
      errorText.textContent = message;
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems && mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}


function updateAttributes(attributes) {
  const body = JSON.stringify({
    attributes,
  });

  fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
}

function updateAttribute(key, value) {
  const attributes = {
    [key]: value,
  };

  updateAttributes(attributes);
}

customElements.define('gift-card-checkbox', class GiftCardCheckbox extends HTMLElement {
  constructor() {
    super();

    const input = this.querySelector('input');
    
    input.addEventListener('click', (event) => {
      const id = input.id;
      const checked = !!event.target.checked ? true : '';

      if (id === 'isCardMessage') {
        const messageInputs = document.querySelector('.cart-gift-card .card-message-inputs');

        if (!checked) {
          messageInputs.classList.remove('active');

          updateAttributes({
            [id]: checked,
            cardSenderName: '',
            cardRecipientName: '',
            cardMessage: '',
          });

          return;
        }

        messageInputs.classList.add('active')
      }

      updateAttribute(id, checked);
    });
  }
});

customElements.define('card-message-input', class CardMessageInput extends HTMLElement {
  constructor() {
    super();
    
    const input = this.querySelector('input');
    
    input.addEventListener('focusout', () => {
      updateAttribute(input.id, input.value);
    });

    input.addEventListener('input', () => {
      if (input.id === 'cardMessage') {
        const cardMessageCount = this.querySelector('.card-message-count');

        cardMessageCount && (cardMessageCount.innerHTML = input.value.length);
      }

      const topic = this.querySelector(`.text-topic--${input.id}`);

      if (topic) {
        input.value.length ? topic.classList.add('text-topic--active') : topic.classList.remove('text-topic--active');
      }
    });
  }
});
